
import React, { useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Patient } from '../../types';
import { Plus, Search, Calendar, ChevronRight, ChevronLeft, Pencil } from 'lucide-react';
import PatientDetails from './PatientDetails';

const PatientList: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: 'M',
    uniqueId: ''
  });

  const fetchPatients = async () => {
    setLoading(true);
    if (!isSupabaseConfigured()) {
      const fakePatients: Patient[] = Array.from({ length: 45 }).map((_, i) => ({
        id: i.toString(),
        first_name: ['Jean', 'Marie', 'Paul', 'Sophie', 'Pierre'][i % 5],
        last_name: ['Dupont', 'Curie', 'Martin', 'Bernard', 'Dubois'][i % 5],
        birth_date: '1990-01-01',
        gender: i % 2 === 0 ? 'M' : 'F',
        unique_id: `P-202312-${(i + 1).toString().padStart(4, '0')}`,
        created_at: new Date().toISOString()
      }));
      setPatients(fakePatients);
    } else {
      const { data, error } = await supabase.from('patients').select('*').order('created_at', { ascending: false });
      if (!error && data) setPatients(data as Patient[]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchPatients(); }, []);

  const handleViewDetails = (patient: Patient) => setSelectedPatient(patient);
  const handleBackToList = () => setSelectedPatient(null);

  const openCreateModal = () => {
    setIsEditing(false);
    setFormData({ firstName: '', lastName: '', birthDate: '', gender: 'M', uniqueId: 'P-' + Date.now().toString().slice(-6) });
    setShowModal(true);
  };

  const openEditModal = (patient: Patient, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setCurrentPatientId(patient.id);
    setFormData({ firstName: patient.first_name, lastName: patient.last_name, birthDate: patient.birth_date, gender: patient.gender, uniqueId: patient.unique_id });
    setShowModal(true);
  };

  const filteredPatients = patients.filter(patient => 
    patient.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.unique_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const currentPatients = filteredPatients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (selectedPatient) return <PatientDetails patient={selectedPatient} onBack={handleBackToList} />;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      
      {/* HEADER FIXE */}
      <div className="p-4 md:p-8 pb-0 shrink-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <h2 className="text-2xl font-bold text-slate-800">Gestion des Individus</h2>
          <button onClick={openCreateModal} className="hidden md:flex bg-indigo-600 text-white px-4 py-2 rounded-lg items-center gap-2 hover:bg-indigo-700 transition-colors shadow-sm">
            <Plus size={18} /> Nouveau Patient
          </button>
        </div>

        {/* BARRE DE FILTRES FIXE */}
        <div className="bg-white rounded-t-xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Rechercher..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          {filteredPatients.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600"><ChevronLeft size={20}/></button>
              <span className="text-sm font-medium px-2">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600"><ChevronRight size={20}/></button>
            </div>
          )}
        </div>
      </div>

      {/* TABLEAU SCROLLABLE */}
      <div className="flex-1 overflow-hidden px-4 md:px-8 pb-4">
        <div className="h-full bg-white border border-t-0 border-slate-200 shadow-sm rounded-b-xl overflow-auto relative">
          <table className="w-full text-left text-sm text-slate-600 border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-100 text-slate-900 font-bold border-b-2 border-slate-200 shadow-sm">
              <tr>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px]">ID Unique</th>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Nom Complet</th>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Sexe</th>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Date de Naissance</th>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Date Création</th>
                <th className="px-6 py-4 uppercase tracking-wider text-[11px] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">Chargement...</td></tr>
              ) : currentPatients.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => handleViewDetails(p)}>
                  <td className="px-6 py-3 font-mono text-indigo-600 font-medium">{p.unique_id}</td>
                  <td className="px-6 py-3 font-medium text-slate-900">{p.last_name.toUpperCase()} {p.first_name}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${p.gender === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>{p.gender}</span>
                  </td>
                  <td className="px-6 py-3 flex items-center gap-2"><Calendar size={14} className="text-slate-400" />{p.birth_date}</td>
                  <td className="px-6 py-3 text-slate-400">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button onClick={(e) => openEditModal(p, e)} className="text-slate-400 hover:text-indigo-600"><Pencil size={16} /></button>
                      <button className="text-indigo-600 font-medium text-xs">Détails</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <button onClick={openCreateModal} className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform active:scale-95"><Plus size={32} /></button>
    </div>
  );
};

export default PatientList;
